<template>
    <nav class="flex w-full mb-4 mt-3" v-if="showBreadcrumbs">
        <div v-for="(crumb, idx) in breadCrumbs">
            <span v-if="idx !== 0" class="mx-2 text-[#333] text-sm">/</span>
            <a v-if="idx < breadCrumbs.length - 1" :href="getAbsoluteURL(crumb)" 
                class="font-normal text-sm cursor-pointer text-[#762123] hover:text-[#762123] underline underline-offset-2">
                {{ crumb.MenuName === 'Quotes List' ? 'Quotes' : crumb.MenuName }}
            </a>
            <span v-else class="font-normal text-sm text-[#464646]">
                {{ crumb.MenuName === 'Quotes List' ? 'Quotes' : crumb.MenuName }}
            </span>
        </div>
    </nav>
</template>

<script setup>
import { onMounted, ref, watch, onUnmounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useRouting } from '@/composables/useRouting.js';

const route = useRoute();
const routing = useRouting();

const urlPath = ref('');
const hashParam = ref('');

const breadCrumbs = ref(window.breadCrumbs || []);
// If you want to pass as a prop, use:
// defineProps({ breadCrumbs: Array })
function getAbsoluteURL(crumb) {
  let url = crumb.ExternalUrl || "";
  if (!url.startsWith("/")) url = "/" + url;
  return url;
}

const showBreadcrumbs = computed(() => breadCrumbs.value.length > 1)

const updateHashParam = () => {
    const fullHash = route?.hash ? route.hash.substring(1) : window.location.hash.substring(1);
    
    if (fullHash.includes('=')) {
        hashParam.value = fullHash.split('=')[1];
    } else {
        hashParam.value = fullHash;
    }
};

onMounted(() => {
    urlPath.value = route?.path ? route.path.substring(1) : window.location.pathname.substring(1);
    updateHashParam();
    mapUrlPathToBreadCrumbs();
    window.addEventListener('hashchange', handleHashChange);
});

onUnmounted(() => {
    window.removeEventListener('hashchange', handleHashChange);
});

const handleHashChange = () => {
    updateHashParam();
    mapUrlPathToBreadCrumbs();
};

watch(() => route?.hash || window.location.hash, () => {
    breadCrumbs.value = window.breadCrumbs || [];
    updateHashParam();
    mapUrlPathToBreadCrumbs();
});

const searchParams = computed(() => window.location.search);

const mapUrlPathToBreadCrumbs = () => {
    switch (urlPath.value) {
        case 'report-ordering':
            // Replace with: Home / Integrations / Eagle View / Order New Report
            breadCrumbs.value = [
                breadCrumbs.value[0], // Keep Home
                { ContentGuid: '', Location: 'integrations', MenuName: 'Integrations', ExternalUrl: '/integrations', isComingSoon: false, IsSelected: false },
                { ContentGuid: '', Location: 'integrations', MenuName: 'Eagle View', ExternalUrl: '/integrations#EagleView', isComingSoon: false, IsSelected: false },
                { ContentGuid: '', Location: '', MenuName: 'Order New Report', ExternalUrl: '#', isComingSoon: false, IsSelected: false }
            ];
            break;
        case 'gaf-report-ordering':
            breadCrumbs.value = [
                breadCrumbs.value[0], 
                { ContentGuid: '', Location: 'integrations', MenuName: 'Integrations', ExternalUrl: '/integrations', isComingSoon: false, IsSelected: false },
                { ContentGuid: '', Location: 'integrations', MenuName: 'QuickMeasure', ExternalUrl: '/integrations#GAF', isComingSoon: false, IsSelected: false },
                { ContentGuid: '', Location: '', MenuName: 'Order QuickMeasure Report', ExternalUrl: '#', isComingSoon: false, IsSelected: false }
            ];
            break;
        case 'new-order': {
            // The Orders crumb from the CMS has no usable link on this page, so point it at the Orders listing.
            const ordersCrumb = breadCrumbs.value.find(c =>
                c.MenuName === 'Orders' || c.Location === 'orders'
            );
            if (ordersCrumb) ordersCrumb.ExternalUrl = 'orders';

            addNewBreadCrumb('orders', 'Create New Order');
            break;
        }
        case 'orders':
            if (hashParam.value) {
                const lastIndex = breadCrumbs.value.length - 1;
                if (breadCrumbs.value.length > 2 &&
                    breadCrumbs.value[lastIndex].MenuName.endsWith('-Order Details')
                ) {
                    breadCrumbs.value[lastIndex].MenuName = `${hashParam.value}-Order Details`;
                } else {
                    addNewBreadCrumb('orders', `${hashParam.value}-Order Details`);
                }
            }
            break;
        case 'invoices':
            if (hashParam.value) {
                if(hashParam.value === 'draftOrders' || hashParam.value === 'orderTemplates') {
                    return;
                } else {
                    // This 'condition' is to prevent the issue to duplicate inclusion of an invoice detail in the breadCrumb.
                    const invoicesCondition = breadCrumbs.value.length > 0 && 
                        breadCrumbs.value[breadCrumbs.value.length - 1].MenuName.endsWith('-Invoice Details');

                    addNewOrReplaceLastOne('invoices', `${hashParam.value}-Invoice Details`, '', invoicesCondition);
                }
            }
            break;
        case 'ordertemplate':
            const fullHash = route?.hash ? route.hash.substring(1) : window.location.search.substring(1);
            
            if (fullHash.includes('TemplateType=NewTemplate')) {
                addNewBreadCrumb('templates', 'Templates', '/templates');
                addNewBreadCrumb('ordertemplate', 'Create a New Template');
                addToolsToBreadCrumb();
            } else if (fullHash.includes('TemplateType=Edit&TemplateId=')) {
                addNewBreadCrumb('templates', 'Templates', '/templates');
                addNewBreadCrumb('ordertemplate', 'Edit Order Template');
                addToolsToBreadCrumb();
            } else if (fullHash.includes('TemplateType=SaveAs&OrderNumber=')) {
                addNewBreadCrumb('templates', 'Templates', '/templates');
                addNewBreadCrumb('ordertemplate', 'Save As A Template');
                addToolsToBreadCrumb();
            }
            break;
        case 'property-reports':
            // Find and rename 'Reports' to 'Homeowner Insights'
            const reportsCrumb = breadCrumbs.value.find(c => 
                c.MenuName === 'Reports' || c.Location === 'reports'
            );
            if (reportsCrumb) {
                reportsCrumb.MenuName = 'Homeowner Insights';
            }
            break;
        case 'quote-release': {
            // Home / Quotes / Quote {id}
            const releaseQuoteId = new URLSearchParams(window.location.search).get('id') || '';
            breadCrumbs.value = [
                breadCrumbs.value[0], // Keep Home
                { ContentGuid: '', Location: 'quotes-list', MenuName: 'Quotes', ExternalUrl: '/quotes-list', isComingSoon: false, IsSelected: false },
                { ContentGuid: '', Location: '', MenuName: releaseQuoteId ? `Quote ${releaseQuoteId}` : 'Quote', ExternalUrl: '#', isComingSoon: false, IsSelected: false }
            ];
            break;
        }
        case 'quotes-details': {
            // Home / Quotes / Quote {id} — insert the missing Quotes (list) crumb and show the quote #
            const detailsQuoteId = new URLSearchParams(window.location.search).get('id') || '';
            const detailsLast = breadCrumbs.value[breadCrumbs.value.length - 1];
            breadCrumbs.value = [
                breadCrumbs.value[0], // Keep Home
                { ContentGuid: '', Location: 'quotes-list', MenuName: 'Quotes', ExternalUrl: '/quotes-list', isComingSoon: false, IsSelected: false },
                { ContentGuid: '', Location: '', MenuName: detailsQuoteId ? `Quote ${detailsQuoteId}` : (detailsLast?.MenuName || 'Quotes Details'), ExternalUrl: '#', isComingSoon: false, IsSelected: false }
            ];
            break;
        }
        case 'quote-request': {
            // Home / Quotes / Quote Request (or Review Quote Request)
            const requestLast = breadCrumbs.value[breadCrumbs.value.length - 1];
            breadCrumbs.value = [
                breadCrumbs.value[0], // Keep Home
                { ContentGuid: '', Location: 'quotes-list', MenuName: 'Quotes', ExternalUrl: '/quotes-list', isComingSoon: false, IsSelected: false },
                { ContentGuid: '', Location: '', MenuName: requestLast?.MenuName || 'Quote Request', ExternalUrl: '#', isComingSoon: false, IsSelected: false }
            ];
            break;
        }
        case 'templates':
            // Add tools at the middle of breadcrumbs
            // Example from "Home/Templates" to "Home/Tools/Templates"
            addNewAndSwapLast('tools', 'Tools', 'templates');
            break;
        case 'solar':
            // Add 'information' at the middle of breadcrumbs
            // Example from "Home/Solar" to "Home/Information/Solar"
            addNewAndSwapLast('information', 'Information');
            break;
        case 'supplier-promotions':
            const _TAB = 'tab'

            if (routing.hasParam(_TAB)) {
                const tabValue = routing.searchParams.value[_TAB];

                if(tabValue === 'manufacturerRewards') {
                    addNewBreadCrumb('supplier-promotions', 'Manufacturer Rewards');
                } else if(tabValue === 'offers') {
                    addNewBreadCrumb('supplier-promotions', 'Offers');
                }
            }
            break;
        case 'lending':
            // Add tools at the middle of breadcrumbs
            // Example from "Home/Lending" to "Home/Tools/Lending"
            addNewAndSwapLast('tools', 'Tools');
            break;
        case 'integrations':
            if (hashParam.value) {
                addNewBreadCrumb('integrations', hashParam.value);
            } else {
                if (breadCrumbs.value.length > 0 && breadCrumbs.value[breadCrumbs.value.length - 1].Location === 'integrations') {
                    breadCrumbs.value.pop();
                }
            }
            break;
        case 'profile/edit-registered-user':
        case 'profile/add-user':
            if (breadCrumbs.value.length > 0) {
                const lastCrumb = breadCrumbs.value.pop();
                addNewBreadCrumb('profile', 'View Users', '/profile/registered-user-list');
                breadCrumbs.value.push(lastCrumb);
            }
            break;
        default:
            break;
    }
}

const addNewBreadCrumb = (location, menuName, externalUrl) => {
    breadCrumbs.value.push({ 
        ContentGuid: '', Location: location, MenuName: menuName, ExternalUrl: externalUrl || '/', isComingSoon: false, IsSelected: false
    });
}

const addNewAndSwapLast = (location, menuName, externalUrl) => {
    addNewBreadCrumb(location, menuName, externalUrl);
    
    // Swap the last two items: [A,B,C] -> [A,C,B]
    if (breadCrumbs.value.length >= 2) {
        const lastIndex = breadCrumbs.value.length - 1;
        const secondLastIndex = lastIndex - 1;
        [breadCrumbs.value[secondLastIndex], breadCrumbs.value[lastIndex]] = 
        [breadCrumbs.value[lastIndex], breadCrumbs.value[secondLastIndex]];
    }
}

// Replace the second breadcrumb with 'Tools' for (create, edit or save as template) 
// Because this info becames wrong from CMS.
const addToolsToBreadCrumb = () => {    
    if (breadCrumbs.value.length > 1) {
        breadCrumbs.value[1].MenuName = 'Tools';
        breadCrumbs.value[1].ExternalUrl = 'templates';
    }
}

const addNewOrReplaceLastOne = (location, menuName, externalUrl, condition) => {
    if (breadCrumbs.value.length > 0 && condition) {
        // Replace the last item if condition is true
        const lastIndex = breadCrumbs.value.length - 1;
        breadCrumbs.value[lastIndex] = {
            ContentGuid: '',
            Location: location,
            MenuName: menuName,
            ExternalUrl: externalUrl || '/',
            isComingSoon: false,
            IsSelected: false
        };
    } else {
        // Add new breadcrumb if condition is false or list is empty
        addNewBreadCrumb(location, menuName, externalUrl);
    }
}

</script>